import type { ReactNode } from 'react';

import { Center, Loader } from '@mantine/core';

import { LoadError } from './LoadError';

interface ReadingStateProps {
  /** What is being read, with its article: "la ficha del cliente", "los adjuntos". */
  what: string;
  /** The first reading, before any data: a small indicator in place of the content. */
  isLoading: boolean;
  /** The error the reading ended with, or null; it shows in place of the content, never beside a "no hay". */
  error: unknown;
  onRetry: () => void;
  /** True only when the reading ended well and brought nothing: then `empty` shows. */
  isEmpty?: boolean;
  /** What the empty state says: what is missing and what to do (rule 8.5). */
  empty?: ReactNode;
  children: ReactNode;
}

/**
 * Guía única, regla 8.1 (`AM-893`): every reading shown on screen passes through one component
 * with three branches, in this order: loading, error, empty. Loading never says "no hay" (8.2); the
 * error is the notice of `LoadError` (8.3); the empty branch appears only when the reading ended
 * well and brought nothing (8.5). The title and the header around it stay visible: the component
 * takes only the place of the content.
 */
export function ReadingState({
  what,
  isLoading,
  error,
  onRetry,
  isEmpty = false,
  empty = null,
  children,
}: ReadingStateProps) {
  if (isLoading) {
    return (
      <Center py="lg" aria-busy="true">
        <Loader size="sm" />
      </Center>
    );
  }
  if (error !== null && error !== undefined) {
    return <LoadError what={what} error={error} onRetry={onRetry} />;
  }
  if (isEmpty) {
    return <>{empty}</>;
  }
  return <>{children}</>;
}
